import { INGREDIENTS } from "./catalog";
import { cleanText, slugify } from "./sanitize";

export interface Goal {
  stat: string;
  amount: number;
}

export interface KitItem {
  item: string;
  count: number;
}

export interface Rank {
  id: string;
  name: string;
  motto: string;
  color: string;
  bold: boolean;
  particle: string | null;
  effects: string[];
  glow: boolean;
  kit: KitItem[];
  goal: Goal;
}

export interface Grant {
  rankId: string;
  player: string;
}

export interface GoalInfo {
  label: string;
  one: string;
  criteria: string[];
  scale: number;
  min: number;
  max: number;
  perTier: number;
}

export const GOALS: Record<string, GoalInfo> = {
  play_hours: { label: "hours played", one: "hour played", criteria: ["minecraft.custom:minecraft.play_time"], scale: 72_000, min: 0, max: 500, perTier: 5 },
  mob_kills: { label: "mobs defeated", one: "mob defeated", criteria: ["minecraft.custom:minecraft.mob_kills"], scale: 1, min: 1, max: 10_000, perTier: 100 },
  diamonds_mined: {
    label: "diamonds mined", one: "diamond mined",
    criteria: ["minecraft.mined:minecraft.diamond_ore", "minecraft.mined:minecraft.deepslate_diamond_ore"],
    scale: 1, min: 1, max: 1_000, perTier: 16,
  },
  walk_km: {
    label: "km walked", one: "km walked",
    criteria: ["minecraft.custom:minecraft.walk_one_cm", "minecraft.custom:minecraft.sprint_one_cm"],
    scale: 100_000, min: 1, max: 1_000, perTier: 10,
  },
  fish_caught: { label: "fish caught", one: "fish caught", criteria: ["minecraft.custom:minecraft.fish_caught"], scale: 1, min: 1, max: 5_000, perTier: 30 },
  animals_bred: { label: "animals bred", one: "animal bred", criteria: ["minecraft.custom:minecraft.animals_bred"], scale: 1, min: 1, max: 5_000, perTier: 30 },
  villager_trades: { label: "villager trades", one: "villager trade", criteria: ["minecraft.custom:minecraft.traded_with_villager"], scale: 1, min: 1, max: 5_000, perTier: 30 },
  raids_won: { label: "raids won", one: "raid won", criteria: ["minecraft.custom:minecraft.raid_win"], scale: 1, min: 1, max: 50, perTier: 0.2 },
  dragon_kills: { label: "Ender Dragons defeated", one: "Ender Dragon defeated", criteria: ["minecraft.killed:minecraft.ender_dragon"], scale: 1, min: 1, max: 5, perTier: 0.12 },
  wither_kills: { label: "Withers defeated", one: "Wither defeated", criteria: ["minecraft.killed:minecraft.wither"], scale: 1, min: 1, max: 5, perTier: 0.09 },
};

export const COLORS = [
  "white", "gray", "dark_gray", "black", "red", "dark_red", "gold", "yellow",
  "green", "dark_green", "aqua", "dark_aqua", "blue", "dark_blue", "light_purple", "dark_purple",
] as const;

export const PARTICLES = [
  "flame", "soul_fire_flame", "heart", "crit", "enchanted_hit", "happy_villager", "electric_spark", "end_rod",
  "dragon_breath", "totem_of_undying", "note", "snowflake", "cherry_leaves", "glow", "witch", "portal", "enchant",
] as const;

export const RANK_EFFECTS = [
  "speed", "haste", "jump_boost", "night_vision", "water_breathing", "fire_resistance", "strength", "luck",
  "dolphins_grace", "slow_falling",
] as const;

export const KIT_ITEMS: readonly string[] = [
  ...INGREDIENTS, "iron_pickaxe", "iron_sword", "iron_axe", "bow", "arrow", "fishing_rod", "bread", "cooked_beef",
  "cooked_porkchop", "golden_apple", "experience_bottle", "firework_rocket", "cod", "salmon", "wheat_seeds",
];

const MAX_NAME = 12;
const MAX_EFFECTS = 2;
const MAX_KIT = 4;
const MAX_KIT_COUNT = 16;

export function sanitizeRank(raw: unknown): Rank {
  const r = (typeof raw === "object" && raw !== null ? raw : {}) as Record<string, unknown>;
  const name = (typeof r.name === "string" ? r.name : "").toUpperCase().replace(/[^A-Z0-9 ]/g, "").replace(/\s+/g, " ")
    .trim().slice(0, MAX_NAME).trim() || "HERO";
  return {
    id: slugify(name),
    name,
    motto: cleanText(r.motto, 80),
    color: pick(r.color, COLORS, "white"),
    bold: r.bold === true,
    particle: (PARTICLES as readonly string[]).includes(String(r.particle)) ? String(r.particle) : null,
    effects: unique(list(r.effects).map(strip).filter((e) => (RANK_EFFECTS as readonly string[]).includes(e))).slice(0, MAX_EFFECTS),
    glow: r.glow === true,
    kit: list(r.kit).map(sanitizeKitItem).filter((k): k is KitItem => k !== null).slice(0, MAX_KIT),
    goal: sanitizeGoal(r.goal),
  };
}

export function sanitizeGrant(raw: unknown): Grant | null {
  const g = (typeof raw === "object" && raw !== null ? raw : {}) as Record<string, unknown>;
  const rankId = typeof g.rankId === "string" && /^[a-z0-9_]{1,32}$/.test(g.rankId) ? g.rankId : null;
  const player = typeof g.player === "string" && /^\.?[A-Za-z0-9_]{1,16}$/.test(g.player) ? g.player : null;
  return rankId && player ? { rankId, player } : null;
}

export function goalText(goal: Goal): string {
  const info = GOALS[goal.stat];
  if (goal.amount === 0) return "Just join the server";
  return `${goal.amount} ${goal.amount === 1 ? info.one : info.label}`;
}

export function rankTier(goal: Goal): number {
  return goal.amount / GOALS[goal.stat].perTier;
}

function sanitizeGoal(raw: unknown): Goal {
  const g = (typeof raw === "object" && raw !== null ? raw : {}) as Record<string, unknown>;
  const stat = typeof g.stat === "string" && g.stat in GOALS ? g.stat : "play_hours";
  const info = GOALS[stat];
  const amount = typeof g.amount === "number" && Number.isFinite(g.amount) ? Math.round(g.amount) : 5;
  return { stat, amount: Math.min(info.max, Math.max(info.min, amount)) };
}

function sanitizeKitItem(raw: unknown): KitItem | null {
  const k = (typeof raw === "object" && raw !== null ? raw : {}) as Record<string, unknown>;
  const item = strip(k.item);
  if (!KIT_ITEMS.includes(item)) return null;
  const count = typeof k.count === "number" && Number.isFinite(k.count) ? Math.round(k.count) : 1;
  return { item, count: Math.min(MAX_KIT_COUNT, Math.max(1, count)) };
}

function pick<T extends string>(raw: unknown, allowed: readonly T[], fallback: T): T {
  return (allowed as readonly string[]).includes(String(raw)) ? (raw as T) : fallback;
}

function list(raw: unknown): unknown[] {
  return Array.isArray(raw) ? raw : [];
}

function strip(raw: unknown): string {
  return typeof raw === "string" ? raw.trim().toLowerCase().replace(/^minecraft:/, "") : "";
}

function unique<T>(items: T[]): T[] {
  return [...new Set(items)];
}

export const BUILT_IN_RANKS: Rank[] = [
  { name: "ROOKIE", motto: "Welcome to the server!", color: "gray", bold: false, particle: null, effects: [], glow: false, kit: [],
    goal: { stat: "play_hours", amount: 0 } },
  { name: "ADVENTURER", motto: "Two hours of adventure", color: "green", bold: false, particle: null, effects: [], glow: false,
    kit: [{ item: "bread", count: 16 }], goal: { stat: "play_hours", amount: 2 } },
  { name: "MINER", motto: "Found 32 diamonds", color: "aqua", bold: false, particle: null, effects: ["haste"], glow: false,
    kit: [{ item: "torch", count: 16 }], goal: { stat: "diamonds_mined", amount: 32 } },
  { name: "WARRIOR", motto: "Defeated 250 mobs", color: "red", bold: false, particle: "crit", effects: ["strength"], glow: false,
    kit: [{ item: "arrow", count: 16 }], goal: { stat: "mob_kills", amount: 250 } },
  { name: "ANGLER", motto: "Caught 100 fish", color: "blue", bold: false, particle: null, effects: ["water_breathing"], glow: false,
    kit: [{ item: "cooked_beef", count: 8 }], goal: { stat: "fish_caught", amount: 100 } },
  { name: "BEASTMASTER", motto: "Bred 100 animals", color: "dark_green", bold: false, particle: "heart", effects: [], glow: false,
    kit: [{ item: "wheat", count: 16 }], goal: { stat: "animals_bred", amount: 100 } },
  { name: "EXPLORER", motto: "Walked 50 kilometers", color: "gold", bold: false, particle: "happy_villager", effects: ["speed"], glow: false,
    kit: [{ item: "firework_rocket", count: 8 }], goal: { stat: "walk_km", amount: 50 } },
  { name: "RAID HERO", motto: "Saved a village from a raid", color: "yellow", bold: true, particle: "totem_of_undying", effects: ["luck"],
    glow: false, kit: [{ item: "emerald", count: 8 }], goal: { stat: "raids_won", amount: 1 } },
  { name: "DRAGONSLAYER", motto: "Defeated the Ender Dragon", color: "dark_purple", bold: true, particle: "dragon_breath", effects: ["slow_falling"],
    glow: true, kit: [{ item: "ender_pearl", count: 4 }], goal: { stat: "dragon_kills", amount: 1 } },
  { name: "LEGEND", motto: "Fifty hours on the server", color: "gold", bold: true, particle: "end_rod", effects: ["night_vision"],
    glow: true, kit: [{ item: "golden_apple", count: 2 }], goal: { stat: "play_hours", amount: 50 } },
  { name: "CHAMPION", motto: "Defeated the Wither. The ultimate rank!", color: "red", bold: true, particle: "flame",
    effects: ["strength", "speed"], glow: true, kit: [{ item: "golden_apple", count: 2 }, { item: "ender_pearl", count: 4 }],
    goal: { stat: "wither_kills", amount: 1 } },
].map((rank) => ({ id: slugify(rank.name), ...rank }));
